/**
 * Public letter verification page.
 *
 * Accessible without authentication so a bank / embassy / any third
 * party can scan the QR at the bottom of an issued letter and confirm
 * it's a real letter from Lucent Dental Company.
 *
 * We deliberately do NOT show the employee's name here — that would
 * leak private info to whoever scanned the QR.  We only reveal:
 *   • Letter type (bilingual)
 *   • Issue + expiry dates
 *   • Status  (valid / expired / revoked / not found)
 *   • Doc number (echo)
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CheckCircle, XCircle, Warning, ShieldCheck, MagnifyingGlass, ClockCounterClockwise, FileText,
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = '/branding/logo.png';

const STATUS_META = {
  valid:     { cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle,
               ar: 'خطاب ساري ✔', en: 'Letter is Valid ✔' },
  expired:   { cls: 'text-amber-700 bg-amber-50 border-amber-200',      Icon: ClockCounterClockwise,
               ar: 'الصلاحية منتهية', en: 'Letter Expired' },
  revoked:   { cls: 'text-red-700 bg-red-50 border-red-200',           Icon: XCircle,
               ar: 'ملغى من قبل الإدارة', en: 'Letter Revoked' },
  not_found: { cls: 'text-slate-600 bg-slate-50 border-slate-200',     Icon: Warning,
               ar: 'الرقم غير موجود', en: 'Not Found' },
};

function fmtDate(iso, isAr) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(isAr ? 'ar-EG' : 'en-GB', {
      timeZone: 'Asia/Riyadh',
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function DateCell({ iso, isAr, testId }) {
  return <span data-testid={testId}>{fmtDate(iso, isAr)}</span>;
}

export default function VerifyLetter() {
  const { docNumber } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const hashParam = params.get('h') || '';

  const [isAr, setIsAr] = useState(true);
  const dir = isAr ? 'rtl' : 'ltr';

  const [loading, setLoading] = useState(!!docNumber);
  const [result, setResult] = useState(null);

  // Manual lookup form (used when there is no doc in the URL)
  const [manualDoc, setManualDoc] = useState('');
  const [manualDate, setManualDate] = useState('');
  const [manualSubmitting, setManualSubmitting] = useState(false);

  useEffect(() => {
    if (!docNumber) return;
    (async () => {
      setLoading(true);
      try {
        const url = `${API}/letters/verify/${encodeURIComponent(docNumber)}${hashParam ? `?h=${encodeURIComponent(hashParam)}` : ''}`;
        const { data } = await axios.get(url);
        setResult(data);
      } catch {
        setResult({ status: 'not_found', doc_number: docNumber });
      } finally {
        setLoading(false);
      }
    })();
  }, [docNumber, hashParam]);

  const handleManualLookup = async (e) => {
    e.preventDefault();
    if (!manualDoc.trim()) return;
    setManualSubmitting(true);
    try {
      const params_ = new URLSearchParams({ doc: manualDoc.trim().toUpperCase() });
      if (manualDate) params_.set('date', manualDate);
      const { data } = await axios.get(`${API}/letters/verify?${params_.toString()}`);
      setResult(data);
    } catch {
      setResult({ status: 'not_found', doc_number: manualDoc.trim() });
    } finally {
      setManualSubmitting(false);
    }
  };

  const meta = useMemo(() => STATUS_META[result?.status] || STATUS_META.not_found, [result]);
  const StatusIcon = meta.Icon;

  // Public PDF URL — only meaningful when the caller has a valid hash
  // (either from the QR link or provided by the manual-verify endpoint
  // via `hash_ok=true`). We only render the embedded viewer when the
  // letter is currently valid.
  const pdfUrl = useMemo(() => {
    if (!result?.doc_number) return null;
    if (result?.status !== 'valid') return null;
    const effectiveHash = hashParam || (result?.hash_ok === true ? '' : null);
    if (effectiveHash === null) return null;
    // For QR flow we always have hashParam. Manual flow: backend returns
    // hash_ok=true only when the entered date matched, so we can request
    // the PDF using a re-sign endpoint. Simplest: only show iframe when
    // the URL hash is present (QR scan). Manual verify still shows meta.
    if (!hashParam) return null;
    return `${API}/letters/pdf/${encodeURIComponent(result.doc_number)}?h=${encodeURIComponent(hashParam)}`;
  }, [result, hashParam]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F2F0FA] via-white to-[#EDE9FE]" dir={dir}>
      {/* Header */}
      <header className="bg-white border-b border-[#C4BFF0]/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
            <div>
              <h1 className="text-[#655dc6] font-semibold text-lg leading-tight">
                {isAr ? 'التحقق من صحة الخطاب' : 'Letter Verification'}
              </h1>
              <p className="text-[10px] text-[#655dc6]/60">
                {isAr ? 'شركة مركز لوسنت لطب الأسنان' : 'Lucent Dental Company'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => setIsAr(!isAr)}
              className="text-[#655dc6] hover:bg-[#EDE9FE]"
              data-testid="verify-lang-toggle"
            >
              {isAr ? 'English' : 'العربية'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {/* If a doc number was in URL → show result */}
        {docNumber && (
          <Card className="border-[#C4BFF0] shadow-sm" data-testid="verify-result-card">
            <CardContent className="py-10 text-center">
              {loading ? (
                <div className="text-[#655dc6]/70 py-8" data-testid="verify-loading">
                  {isAr ? 'جاري التحقق…' : 'Verifying…'}
                </div>
              ) : (
                <>
                  <div className={`inline-flex flex-col items-center gap-3 px-8 py-6 rounded-2xl border ${meta.cls}`}
                       data-testid={`verify-status-${result?.status || 'unknown'}`}>
                    <StatusIcon size={64} weight="duotone" />
                    <div className="text-2xl font-semibold">
                      {isAr ? meta.ar : meta.en}
                    </div>
                    <div className="text-xs opacity-80">
                      {isAr
                        ? 'خطاب رسمي صادر من شركة مركز لوسنت لطب الأسنان'
                        : 'Official letter issued by Lucent Dental Company'}
                    </div>
                  </div>

                  <dl className="mt-8 grid grid-cols-2 gap-x-8 gap-y-3 max-w-md mx-auto text-sm text-start">
                    <dt className="text-[#655dc6]/70">{isAr ? 'رقم الوثيقة' : 'Doc Number'}</dt>
                    <dd className="font-mono text-[#2D2A5C]" data-testid="verify-doc-number">
                      {result?.doc_number || '—'}
                    </dd>

                    <dt className="text-[#655dc6]/70">{isAr ? 'نوع الخطاب' : 'Letter Type'}</dt>
                    <dd className="text-[#2D2A5C]" data-testid="verify-letter-type">
                      {isAr ? (result?.type_label_ar || '—') : (result?.type_label_en || '—')}
                    </dd>

                    <dt className="text-[#655dc6]/70">{isAr ? 'تاريخ الإصدار' : 'Issue Date'}</dt>
                    <dd className="text-[#2D2A5C]">
                      <DateCell iso={result?.issued_at} isAr={isAr} testId="verify-issue-date" />
                    </dd>

                    <dt className="text-[#655dc6]/70">{isAr ? 'تاريخ الانتهاء' : 'Expires'}</dt>
                    <dd className="text-[#2D2A5C]">
                      <DateCell iso={result?.expires_at} isAr={isAr} testId="verify-expiry-date" />
                    </dd>

                    {result?.status === 'revoked' && (
                      <>
                        <dt className="text-red-700">{isAr ? 'سبب الإلغاء' : 'Revoke Reason'}</dt>
                        <dd className="text-red-700 text-xs" data-testid="verify-revoke-reason">
                          {result?.revoke_reason || (isAr ? 'غير محدد' : 'Not specified')}
                        </dd>
                      </>
                    )}
                  </dl>

                  {hashParam && result?.hash_ok === false && (
                    <div className="mt-6 text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg py-2 px-4 inline-block">
                      {isAr
                        ? '⚠ تحذير: التوقيع الأمني في الرابط لا يطابق الوثيقة. قد يكون الرابط مزوّراً.'
                        : '⚠ Warning: security signature mismatch — the URL may be forged.'}
                    </div>
                  )}

                  <div className="mt-8">
                    <Button
                      variant="outline"
                      onClick={() => navigate('/verify')}
                      className="border-[#C4BFF0] text-[#655dc6]"
                      data-testid="verify-lookup-another"
                    >
                      <MagnifyingGlass size={16} className="me-2" />
                      {isAr ? 'التحقق من خطاب آخر' : 'Verify another letter'}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Embedded letter viewer — only shown when the QR link has a
            matching hash AND the letter is currently valid. Third
            parties can then read the actual letter after we've told
            them it is authentic. */}
        {pdfUrl && !loading && (
          <Card className="border-[#C4BFF0] shadow-sm" data-testid="verify-pdf-card">
            <CardContent className="py-6">
              <div className="flex items-center gap-2 mb-3">
                <FileText size={20} weight="duotone" className="text-[#655dc6]" />
                <h3 className="text-sm font-semibold text-[#655dc6]">
                  {isAr ? 'الخطاب' : 'Letter Preview'}
                </h3>
              </div>
              <div className="rounded-lg overflow-hidden border border-[#C4BFF0] bg-white">
                <iframe
                  src={pdfUrl}
                  title="letter-preview"
                  className="w-full h-[70vh]"
                  data-testid="verify-pdf-frame"
                />
              </div>
              <div className="mt-3 flex justify-end">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#655dc6] underline"
                  data-testid="verify-pdf-open"
                >
                  {isAr ? 'فتح في تبويب جديد ⧉' : 'Open in new tab ⧉'}
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manual lookup form (fallback) */}
        {!docNumber && (
          <Card className="border-[#C4BFF0] shadow-sm" data-testid="verify-manual-card">
            <CardContent className="py-8">
              <div className="text-center mb-6">
                <ShieldCheck size={48} weight="duotone" className="mx-auto text-[#655dc6] mb-3" />
                <h2 className="text-xl font-semibold text-[#655dc6]">
                  {isAr ? 'التحقق من صحة خطاب' : 'Verify a Letter'}
                </h2>
                <p className="text-xs text-[#655dc6]/70 mt-2 max-w-md mx-auto">
                  {isAr
                    ? 'أدخل رقم الوثيقة كما هو مكتوب أسفل الخطاب، مع تاريخ الإصدار للتأكيد.'
                    : 'Enter the document number printed under the letter along with its issue date.'}
                </p>
              </div>

              <form onSubmit={handleManualLookup} className="max-w-md mx-auto space-y-4">
                <div>
                  <Label className="text-xs text-[#655dc6]">
                    {isAr ? 'رقم الوثيقة' : 'Document Number'}
                  </Label>
                  <Input
                    value={manualDoc}
                    onChange={(e) => setManualDoc(e.target.value)}
                    placeholder="LDC-2026-000123"
                    className="font-mono uppercase"
                    autoFocus
                    required
                    data-testid="verify-manual-doc"
                  />
                </div>
                <div>
                  <Label className="text-xs text-[#655dc6]">
                    {isAr ? 'تاريخ الإصدار' : 'Issue Date'}
                  </Label>
                  <Input
                    type="date"
                    value={manualDate}
                    onChange={(e) => setManualDate(e.target.value)}
                    required
                    data-testid="verify-manual-date"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={manualSubmitting || !manualDoc.trim()}
                  className="w-full bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
                  data-testid="verify-manual-submit"
                >
                  <MagnifyingGlass size={16} />
                  {isAr ? 'تحقق' : 'Verify'}
                </Button>
              </form>

              {result && (
                <div className="mt-6 pt-6 border-t border-[#C4BFF0]/50 text-center">
                  <div className={`inline-flex flex-col items-center gap-2 px-6 py-4 rounded-2xl border ${meta.cls}`}
                       data-testid={`verify-manual-status-${result?.status || 'unknown'}`}>
                    <StatusIcon size={40} weight="duotone" />
                    <div className="text-lg font-semibold">
                      {isAr ? meta.ar : meta.en}
                    </div>
                    {result?.status !== 'not_found' && (
                      <div className="text-xs opacity-80 flex flex-wrap items-center justify-center gap-2">
                        <span>{isAr ? result?.type_label_ar : result?.type_label_en}</span>
                        <span>·</span>
                        <DateCell iso={result?.issued_at} isAr={isAr} testId="verify-manual-issue-date" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <p className="text-center text-[10px] text-[#655dc6]/50">
          {isAr
            ? 'هذه صفحة تحقق رسمية لا تُظهر أي بيانات شخصية للموظف حفاظاً على الخصوصية.'
            : 'This is an official verification page. No personal employee data is disclosed for privacy.'}
        </p>
      </main>
    </div>
  );
}
